import { apiClient, extractApiError } from './api'
import {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  OTPRequest,
  OTPVerificationRequest,
  ApiResponse,
} from '@/types'

class AuthService {
  /**
   * Connexion utilisateur
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>(
        '/auth/login',
        credentials
      )
      return response.data.data!
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Inscription utilisateur
   */
  async register(userData: RegisterRequest): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/register', userData)
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Déconnexion
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/logout')
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Rafraîchir le token
   */
  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>(
        '/auth/refresh-token',
        { refreshToken }
      )
      return response.data.data!
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Envoyer un code OTP
   */
  async sendOTP(request: OTPRequest): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/request-otp', request)
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Vérifier un code OTP
   */
  async verifyOTP(request: OTPVerificationRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>(
        '/auth/otp/verify',
        request
      )
      return response.data.data!
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Demande de réinitialisation de mot de passe
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/forgot-password', {
        identifier: email,
      })
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Réinitialiser le mot de passe
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/reset-password', {
        token,
        password: newPassword,
      })
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Changer le mot de passe
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/change-password', {
        currentPassword,
        newPassword,
      })
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Obtenir le profil utilisateur
   */
  async getProfile(): Promise<User> {
    try {
      // Use /auth/me as it returns the full profile with relations now
      const response = await apiClient.get<ApiResponse<User>>('/auth/me')
      return response.data.data!
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Mettre à jour le profil utilisateur
   */
  async updateProfile(updates: Partial<User>): Promise<User> {
    try {
      const response = await apiClient.put<ApiResponse<User>>(
        '/users/profile',
        updates
      )
      return response.data.data!
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Ajouter un dépendant
   */
  async addDependent(dependentData: any): Promise<User> {
    try {
      const response = await apiClient.post<ApiResponse<User>>(
        '/users/family/add-dependent',
        dependentData
      )
      return response.data.data!
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Demander une modification de coordonnées
   */
  async requestContactChange(data: any): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/users/request-contact-change', data)
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Configuration du code PIN
   */
  async setupPIN(pin: string): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/pin/setup', { pin })
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Changer le code PIN
   */
  async changePIN(currentPin: string, newPin: string): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>('/auth/pin/change', {
        currentPin,
        newPin,
      })
    } catch (error) {
      throw extractApiError(error)
    }
  }

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  async checkAuth(): Promise<User | null> {
    try {
      const response = await apiClient.get<ApiResponse<User>>('/auth/me')
      return response.data.data!
    } catch (error) {
      return null
    }
  }
}

export const authService = new AuthService()
export default authService